import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { VideoFactoryCategory } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const StyledSidePanel = styled(SidePanel)`
  width: 600px;
  textarea {
    min-height: 400px;
  }
`;

type FormDataT = {
  topic: string;
  category_id: null | number;
};

interface Props {
  onClose: VoidFunction;
  projectId: string;
}

const DraftForm: FC<Props> = ({ onClose, projectId }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: categories } = useQuery(
    ['video_factory_categories'],
    () =>
      api.get<never, VideoFactoryCategory[]>('/v1/video-factory/categories'),
    {
      staleTime: 120_000,
      select: (data) => data.map((c) => ({ label: c.name, value: c.id })),
    },
  );

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      topic: '',
      category_id: null,
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post(`/v1/video-factory/projects/${projectId}/draft`, data);

        await queryClient.invalidateQueries(['video_factory_projects']);

        navigate('.', { relative: 'path', replace: true });

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <StyledSidePanel heading={'Project Draft'} onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT, number | null>
                control={control}
                label="Category"
                type="select"
                name="category_id"
                options={[
                  { label: 'DEFAULT', value: null },
                  ...(categories ?? []),
                ]}
              />
              <FormField<FormDataT>
                control={control}
                label="Topic"
                type="textarea"
                name="topic"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Save and generate
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </StyledSidePanel>
    </Modal>
  );
};

export default DraftForm;
